/**
 * Manual-review sources (owner decision, 28 Oct 2026). TikTok, X and the Google Business listing restrict automated
 * access, so the collectors never contact them: no profile read, no search, no Places call. The collector answers
 * `manual_review_required` without any network call, and the report and portal show one neutral notice with a mailto
 * link carrying the audit id. No score, no estimate, no figure is produced for these sources.
 */
export const MANUAL_REVIEW_PLATFORMS = ['tiktok', 'x', 'google_business'] as const;
export type ManualReviewPlatform = (typeof MANUAL_REVIEW_PLATFORMS)[number];
export const isManualReviewPlatform = (p: string): p is ManualReviewPlatform => (MANUAL_REVIEW_PLATFORMS as readonly string[]).includes(p);

export const MANUAL_REVIEW_EMAIL = 'support@quixomarketing.com';

const NAMES: Record<ManualReviewPlatform, { en: string; ar: string }> = {
  tiktok: { en: 'TikTok', ar: 'تيك توك' },
  x: { en: 'X', ar: 'X' },
  google_business: { en: 'Google Business listing', ar: 'نشاط Google التجاري' },
};

export interface ManualReviewNotice {
  platforms: ManualReviewPlatform[];
  en: string;
  ar: string;
  /** mailto link; the subject carries the audit id */
  mailto: string;
}

/** The neutral notice for the sources that need a person, in English and Arabic. Null when none does. */
export function manualReviewNotice(platforms: readonly string[], auditId: string): ManualReviewNotice | null {
  const list = MANUAL_REVIEW_PLATFORMS.filter((p) => platforms.includes(p));
  if (!list.length) return null;
  const subject = encodeURIComponent(`Manual audit ${auditId}`);
  return {
    platforms: list,
    en: `Manual audit required for ${list.map((p) => NAMES[p].en).join(' / ')} — contact ${MANUAL_REVIEW_EMAIL}`,
    ar: `يلزم تدقيق يدوي لـ ${list.map((p) => NAMES[p].ar).join(' / ')} — تواصل معنا على ${MANUAL_REVIEW_EMAIL}`,
    mailto: `mailto:${MANUAL_REVIEW_EMAIL}?subject=${subject}`,
  };
}

/** The notice for an audit's not-measured ledger: platforms come from the subject's profiles plus the Google listing row. */
export function manualReviewFromAnalysis(a: { auditId: string; subject: { profiles: { platform: string }[] }; coverage: { notMeasured: { ref: string; reason: string }[] } }): ManualReviewNotice | null {
  const flagged = a.coverage.notMeasured.filter((n) => n.reason.startsWith('manual_review_required'));
  if (!flagged.length) return null;
  const platforms = new Set<string>();
  for (const n of flagged) {
    if (/googleBusiness/.test(n.ref)) platforms.add('google_business');
    const m = /profiles\.(\d+)/.exec(n.ref);
    const p = m ? a.subject.profiles[Number(m[1])]?.platform : undefined;
    if (p) platforms.add(p);
  }
  return manualReviewNotice([...platforms], a.auditId);
}
